/**
 * Synthesized notification sounds using Web Audio API.
 * Different tone patterns for restaurant vs driver so each role has a distinctive alert.
 */

let ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
    } catch {
      return null;
    }
  }
  // Some browsers suspend context until a user gesture; resume best-effort
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

function beep({ freq, duration = 0.16, delay = 0, type = "sine", volume = 0.25 }) {
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

/**
 * Restaurant kitchen bell — a bright two-tone chime.
 * "Ding-dong" pattern like a service bell.
 */
export function playRestaurantDing() {
  // Two chime tones + a soft high sparkle
  beep({ freq: 1318, duration: 0.28, delay: 0.0, type: "triangle", volume: 0.32 }); // E6
  beep({ freq: 987,  duration: 0.36, delay: 0.18, type: "triangle", volume: 0.32 }); // B5
  beep({ freq: 1760, duration: 0.12, delay: 0.02, type: "sine", volume: 0.10 }); // A6 sparkle
}

/**
 * Driver offer alert — three quick ascending "beeps" reminiscent of a ride-share ping.
 * Repeats twice for extra prominence.
 */
export function playDriverDing() {
  // Burst 1
  beep({ freq: 660,  duration: 0.12, delay: 0.0,  type: "square", volume: 0.22 });
  beep({ freq: 880,  duration: 0.12, delay: 0.15, type: "square", volume: 0.24 });
  beep({ freq: 1174, duration: 0.22, delay: 0.30, type: "square", volume: 0.26 });
  // Burst 2 (louder / higher for urgency)
  beep({ freq: 880,  duration: 0.12, delay: 0.65, type: "square", volume: 0.24 });
  beep({ freq: 1174, duration: 0.12, delay: 0.80, type: "square", volume: 0.26 });
  beep({ freq: 1568, duration: 0.28, delay: 0.95, type: "square", volume: 0.28 });
}

/** Success chime — used after proof-of-delivery captured. */
export function playSuccessChime() {
  beep({ freq: 880,  duration: 0.16, delay: 0,    type: "sine", volume: 0.22 });
  beep({ freq: 1319, duration: 0.24, delay: 0.14, type: "sine", volume: 0.22 });
}

/**
 * Caribbean-vibe looping alert for new order requests.
 * - Plays a syncopated steel-drum-like arpeggio in the pentatonic-major E scale.
 * - Starts at 30% volume, ramps linearly to 100% over 30 seconds.
 * - Returns a controller with `.stop()` that fades out cleanly.
 */
export function startCaribbeanAlert() {
  const c = getCtx();
  if (!c) return { stop: () => {} };

  const master = c.createGain();
  const t0 = c.currentTime;
  master.gain.setValueAtTime(0.30, t0);
  master.gain.linearRampToValueAtTime(1.00, t0 + 30);
  master.connect(c.destination);

  // Two-bar syncopated pattern — feels like a subtle steelpan groove.
  const BAR = 1.6;
  const pattern = [
    { f: 659,  d: 0.20, o: 0.00 }, // E5
    { f: 784,  d: 0.20, o: 0.20 }, // G5
    { f: 988,  d: 0.20, o: 0.42 }, // B5
    { f: 1319, d: 0.28, o: 0.62 }, // E6
    { f: 988,  d: 0.20, o: 0.96 }, // B5 (offbeat)
    { f: 1175, d: 0.20, o: 1.16 }, // D6
    { f: 1568, d: 0.32, o: 1.36 }, // G6 accent
  ];

  const voice = (freq, dur, when, gain = 0.28) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, when);
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(gain, when + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0005, when + dur);
    osc.connect(g).connect(master);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  };

  const scheduleBar = (barStart) => {
    pattern.forEach(({ f, d, o }) => voice(f, d, barStart + o));
    // Add a soft sub kick on beats 1 & 3 to give the loop rhythm
    const sub = c.createOscillator();
    const sg = c.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(110, barStart);
    sub.frequency.exponentialRampToValueAtTime(55, barStart + 0.18);
    sg.gain.setValueAtTime(0.18, barStart);
    sg.gain.exponentialRampToValueAtTime(0.001, barStart + 0.22);
    sub.connect(sg).connect(master);
    sub.start(barStart);
    sub.stop(barStart + 0.24);
  };

  let stopped = false;
  let scheduledUntil = t0 + 0.05;
  const tick = () => {
    if (stopped) return;
    const horizon = c.currentTime + 2.0;
    while (scheduledUntil < horizon) {
      scheduleBar(scheduledUntil);
      scheduledUntil += BAR;
    }
    setTimeout(tick, 250);
  };
  tick();

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      const now = c.currentTime;
      try {
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(0.0001, now + 0.18);
      } catch { /* noop */ }
      setTimeout(() => { try { master.disconnect(); } catch { /* noop */ } }, 250);
    },
  };
}
